import React, { createContext, useContext, useEffect, useState } from 'react';
import { demoContent, isLayoutDemo } from '../demo';

// Defaults mirror public/site-content.json so product actions render before it loads.
const DEFAULT_PRODUCTS = [
  { slug: 'learn', name: 'Agentec Learn', links: [{ label: { en: 'Open Agentec Learn', zh: '打开 Agentec Learn' }, url: '/learn/', platform: 'Web' }] },
  { slug: 'nexus', name: 'Agentec Nexus' },
  { slug: 'agentec-one', name: 'Agentec One', links: [
    { label: { en: 'Download for Windows', zh: '下载 Windows 版' }, url: '/downloads/AgentecOne-0.6.7-Windows-x86_64-Setup.exe', platform: 'Windows' },
    { label: { en: 'Open account portal', zh: '打开账户中心' }, url: '/one/', platform: 'Web' }
  ] }
];
const DEFAULT_CONTENT = { products: DEFAULT_PRODUCTS, film: {} };
const SiteContentContext = createContext({ ...DEFAULT_CONTENT, loading: true, error: null });
let contentRequest;

const localizedText = (value) => {
  if (!value || typeof value !== 'object') return undefined;
  const result = {};
  ['en', 'zh'].forEach((language) => {
    if (typeof value[language] === 'string' && value[language].trim()) {
      result[language] = value[language].trim();
    }
  });
  return Object.keys(result).length ? result : undefined;
};

const safeUrl = (value) => {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  const url = value.trim();
  if (url.startsWith('/') && !url.startsWith('//') && !url.includes('\\')) return url;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && !parsed.username && !parsed.password ? url : undefined;
  } catch {
    return undefined;
  }
};

const normalizeProduct = (value, fallback) => {
  const product = { ...fallback };
  if (!value || typeof value !== 'object') return product;
  if (typeof value.name === 'string' && value.name.trim()) product.name = value.name.trim();
  ['tagline', 'summary', 'audience', 'notes'].forEach((key) => {
    const text = localizedText(value[key]);
    if (text) product[key] = text;
  });
  if (['available', 'beta', 'coming-soon'].includes(value.status)) product.status = value.status;
  if (safeUrl(value.logo)) product.logo = safeUrl(value.logo);
  if (Array.isArray(value.features)) {
    product.features = value.features.filter((item) => item && localizedText(item.title)).map((item) => ({
      title: localizedText(item.title),
      description: localizedText(item.description)
    }));
  }
  if (Array.isArray(value.screenshots)) {
    product.screenshots = value.screenshots.filter((item) => item && safeUrl(item.src) && localizedText(item.alt)).map((item) => ({
      src: safeUrl(item.src),
      alt: localizedText(item.alt),
      caption: localizedText(item.caption),
      cardCaption: localizedText(item.cardCaption)
    }));
  }
  if (Array.isArray(value.links)) {
    product.links = value.links.filter((item) => item && safeUrl(item.url) && localizedText(item.label)).map((item) => ({
      label: localizedText(item.label),
      url: safeUrl(item.url),
      ...(typeof item.platform === 'string' ? { platform: item.platform.trim() } : {})
    }));
  }
  return product;
};

const normalizeFilm = (value) => {
  if (!value || !['youtube', 'bilibili', 'file'].includes(value.type)) return {};
  const src = safeUrl(value.src);
  const fallbackUrl = safeUrl(value.fallbackUrl);
  if (!src || !fallbackUrl) return {};
  if (value.type !== 'file') {
    let parsed;
    try { parsed = new URL(src); } catch { return {}; }
    if (value.type === 'youtube' && (!['www.youtube.com', 'youtube.com', 'www.youtube-nocookie.com', 'youtube-nocookie.com'].includes(parsed.hostname) || !/^\/embed\/[\w-]+$/.test(parsed.pathname))) return {};
    if (value.type === 'bilibili' && (parsed.hostname !== 'player.bilibili.com' || parsed.pathname !== '/player.html' || !/^BV[\w]+$/.test(parsed.searchParams.get('bvid') || ''))) return {};
  }
  const film = { type: value.type, src, fallbackUrl };
  const poster = safeUrl(value.poster);
  if (poster) film.poster = poster;
  ['title', 'description', 'transcript'].forEach((key) => {
    const text = localizedText(value[key]);
    if (text) film[key] = text;
  });
  if (Array.isArray(value.captions)) {
    film.captions = value.captions.filter((item) => item && safeUrl(item.src) && typeof item.language === 'string' && typeof item.label === 'string').map((item) => ({
      src: safeUrl(item.src), language: item.language, label: item.label
    }));
  }
  return film;
};

const normalizeContent = (value) => {
  if (!value || typeof value !== 'object') throw new Error('Site content must be a JSON object.');
  const configuredProducts = Array.isArray(value.products) ? value.products : [];
  return {
    products: DEFAULT_PRODUCTS.map((fallback) => normalizeProduct(configuredProducts.find((product) => product && (product.slug === fallback.slug || (fallback.slug === 'nexus' && product.slug === 'nexcus'))), fallback)),
    film: normalizeFilm(value.film)
  };
};

const loadSiteContent = () => {
  if (!contentRequest) {
    contentRequest = fetch('/site-content.json', { cache: 'no-cache', credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error('Site content could not be loaded.');
        return response.json();
      })
      .then(normalizeContent);
  }
  return contentRequest;
};

export const SiteContentProvider = ({ children }) => {
  const [state, setState] = useState({ ...(isLayoutDemo ? demoContent : DEFAULT_CONTENT), loading: !isLayoutDemo, error: null });
  useEffect(() => {
    if (isLayoutDemo) return undefined;
    let active = true;
    loadSiteContent().then((content) => {
      if (active) setState({ ...content, loading: false, error: null });
    }).catch((error) => {
      if (active) setState({ ...DEFAULT_CONTENT, loading: false, error: error.message });
    });
    return () => { active = false; };
  }, []);
  return <SiteContentContext.Provider value={state}>{children}</SiteContentContext.Provider>;
};

export const useSiteContent = () => useContext(SiteContentContext);
